import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import VideoPanel from './components/VideoPanel';
import ChatPanel from './components/ChatPanel';
import PatientInfoPanel from './components/PatientInfoPanel';
import ConsultationHeader from './components/ConsultationHeader';
import MobileVideoControls from './components/MobileVideoControls';

const VideoConsultation = () => {
  const [isCallActive, setIsCallActive] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [isPatientInfoCollapsed, setIsPatientInfoCollapsed] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isMobileView, setIsMobileView] = useState(false);

  // Mock patient data
  const patient = {
    id: "PT-2024-001",
    name: "Emily Chen",
    age: 34,
    gender: "Female",
    dateOfBirth: "03/15/1990",
    phone: "(555) 123-4567",
    location: "San Francisco, CA",
    insurance: "Blue Cross Blue Shield",
    emergencyContact: "(555) 987-6543"
  };

  const appointmentTime = new Date();
  const consultationType = "Follow-up";

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Call duration timer
  useEffect(() => {
    let interval;
    if (isCallActive) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCallActive]);

  const handleToggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleToggleCamera = () => {
    setIsCameraOff(!isCameraOff);
  };

  const handleToggleScreenShare = () => {
    setIsScreenSharing(!isScreenSharing);
  };

  const handleEndCall = () => {
    setIsCallActive(false);
    setCallDuration(0);
  };

  const handleToggleRecording = () => {
    setIsRecording(!isRecording);
  };

  const handleEndConsultation = () => {
    setIsCallActive(false);
    setCallDuration(0);
  };

  const handleUpdateVitals = (vitals) => {
    console.log('Updating vitals:', vitals);
  };

  const handleAddObservation = (observation) => {
    console.log('Adding observation:', observation);
  };

  const handleToggleChat = () => {
    setIsChatCollapsed(!isChatCollapsed);
  };

  const handleTogglePatientInfo = () => {
    setIsPatientInfoCollapsed(!isPatientInfoCollapsed);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Video Consultation - HealthGuard Pro</title>
        <meta name="description" content="Secure video consultation platform for healthcare providers and patients" />
      </Helmet>
      <Header />
      <div className="pt-16 h-screen flex flex-col">
        <ConsultationHeader
          patient={patient}
          appointmentTime={appointmentTime}
          consultationType={consultationType}
          isRecording={isRecording}
          onToggleRecording={handleToggleRecording}
          onEndConsultation={handleEndConsultation}
        />

        <div className="flex-1 flex overflow-hidden">
          {/* Main Video Area */}
          <div className="flex-1 relative">
            <VideoPanel
              isCallActive={isCallActive}
              isMuted={isMuted}
              isCameraOff={isCameraOff}
              isScreenSharing={isScreenSharing}
              onToggleMute={handleToggleMute}
              onToggleCamera={handleToggleCamera}
              onToggleScreenShare={handleToggleScreenShare}
              onEndCall={handleEndCall}
              patientName={patient?.name}
              callDuration={callDuration}
            />

            {/* Mobile Controls Overlay */}
            {isMobileView && (
              <MobileVideoControls
                isMuted={isMuted}
                isCameraOff={isCameraOff}
                isScreenSharing={isScreenSharing}
                onToggleMute={handleToggleMute}
                onToggleCamera={handleToggleCamera}
                onToggleScreenShare={handleToggleScreenShare}
                onEndCall={handleEndCall}
                onToggleChat={handleToggleChat}
                onTogglePatientInfo={handleTogglePatientInfo}
                callDuration={callDuration}
              />
            )}
          </div>

          {/* Desktop Side Panels */}
          {!isMobileView && (
            <>
              <ChatPanel
                isCollapsed={isChatCollapsed}
                onToggleCollapse={() => setIsChatCollapsed(!isChatCollapsed)}
              />

              <PatientInfoPanel
                patient={patient}
                onUpdateVitals={handleUpdateVitals}
                onAddObservation={handleAddObservation}
              />
            </>
          )}
        </div>

        {/* Mobile Slide-out Panels */}
        {isMobileView && (
          <>
            {/* Mobile Chat Panel */}
            <div className={`fixed inset-y-0 right-0 z-50 transform transition-transform duration-300 ${
              isChatCollapsed ? 'translate-x-full' : 'translate-x-0'
            }`}>
              <ChatPanel
                isCollapsed={false}
                onToggleCollapse={handleToggleChat}
              />
            </div>

            {/* Mobile Patient Info Panel */}
            <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ${
              isPatientInfoCollapsed ? '-translate-x-full' : 'translate-x-0'
            }`}>
              <PatientInfoPanel
                patient={patient}
                onUpdateVitals={handleUpdateVitals}
                onAddObservation={handleAddObservation}
              />
            </div>

            {/* Mobile Overlay */}
            {(!isChatCollapsed || !isPatientInfoCollapsed) && (
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => {
                  setIsChatCollapsed(true);
                  setIsPatientInfoCollapsed(true);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoConsultation;